import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Wifi, 
  WifiOff, 
  Signal, 
  Clock, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Info,
  Zap
} from 'lucide-react';
import { useNetworkStatus, NetworkStatus, NetworkDiagnostics } from '@/hooks/useNetworkStatus';
import { cn } from '@/lib/utils';

interface NetworkStatusMonitorProps {
  showDiagnostics?: boolean;
  compact?: boolean;
  onNetworkRecovered?: () => void;
  onNetworkLost?: () => void;
  className?: string;
}

const NetworkStatusMonitor: React.FC<NetworkStatusMonitorProps> = ({
  showDiagnostics = false,
  compact = false,
  onNetworkRecovered,
  onNetworkLost,
  className
}) => {
  const {
    networkStatus,
    diagnostics,
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    runDiagnostics,
    assessConnectionQuality
  } = useNetworkStatus({
    enableDiagnostics: showDiagnostics,
    diagnosticsInterval: 30000,
    monitoringEnabled: true
  });

  const [previousStatus, setPreviousStatus] = useState<NetworkStatus | null>(null);
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);

  // Monitor network status changes
  useEffect(() => {
    if (previousStatus) {
      const wasOffline = !previousStatus.isOnline;
      const isNowOnline = networkStatus.isOnline;

      if (wasOffline && isNowOnline) {
        onNetworkRecovered?.();
      } else if (!wasOffline && !isNowOnline) {
        onNetworkLost?.();
      }
    }
    setPreviousStatus(networkStatus);
  }, [networkStatus.isOnline, onNetworkRecovered, onNetworkLost, previousStatus]);

  // Auto-start monitoring
  useEffect(() => {
    if (!isMonitoring) {
      startMonitoring();
    }
    return () => stopMonitoring();
  }, [isMonitoring, startMonitoring, stopMonitoring]);

  const getConnectionIcon = () => {
    if (!networkStatus.isOnline) return <WifiOff className="h-5 w-5 text-red-500" />;

    switch (networkStatus.connectionQuality) {
      case 'excellent':
        return <Wifi className="h-5 w-5 text-green-500" />;
      case 'good':
        return <Signal className="h-5 w-5 text-blue-500" />;
      case 'poor':
        return <Signal className="h-5 w-5 text-yellow-500" />;
      default:
        return <Signal className="h-5 w-5 text-gray-500" />;
    }
  };

  const getConnectionBadge = () => {
    if (!networkStatus.isOnline) {
      return <Badge variant="destructive">Offline</Badge>;
    }

    const qualityConfig = {
      excellent: { variant: 'default' as const, color: 'bg-green-500' },
      good: { variant: 'secondary' as const, color: 'bg-blue-500' },
      poor: { variant: 'outline-solid' as const, color: 'bg-yellow-500' },
      unknown: { variant: 'outline-solid' as const, color: 'bg-gray-500' }
    };

    const config = qualityConfig[networkStatus.connectionQuality] || qualityConfig.unknown;

    return (
      <Badge variant={config.variant} className="capitalize">
        <div className={cn("w-2 h-2 rounded-full mr-2", config.color)} />
        {networkStatus.connectionQuality}
      </Badge>
    );
  };

  const formatSpeed = (bytesPerSecond: number | null): string => {
    if (!bytesPerSecond) return 'Unknown';
    
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`;
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  const formatLatency = (ms: number | null): string => {
    if (!ms) return 'Unknown';
    return `${ms.toFixed(0)}ms`;
  };

  const handleRunDiagnostics = async () => {
    setIsRunningDiagnostics(true);
    try {
      await runDiagnostics();
    } finally {
      setIsRunningDiagnostics(false);
    }
  };

  const getConnectionDetails = () => {
    const details = [];

    if (networkStatus.effectiveType !== 'unknown') {
      details.push(`Type: ${networkStatus.effectiveType.toUpperCase()}`);
    }

    if (networkStatus.downlink) {
      details.push(`Speed: ${networkStatus.downlink} Mbps`);
    }

    if (networkStatus.rtt) {
      details.push(`Latency: ${networkStatus.rtt}ms`);
    }

    if (networkStatus.saveData) {
      details.push('Data Saver: On');
    }

    return details;
  };

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {getConnectionIcon()}
        {getConnectionBadge()}
        {showDiagnostics && diagnostics.errors.length > 0 && (
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
        )}
      </div>
    );
  }

  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getConnectionIcon()}
            <div>
              <CardTitle className="text-lg">Network Status</CardTitle>
              <CardDescription>
                Real-time connection monitoring
              </CardDescription>
            </div>
          </div>
          {getConnectionBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Connection Details */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Connection Info</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              {getConnectionDetails().map((detail, index) => (
                <div key={index}>{detail}</div>
              ))}
              {getConnectionDetails().length === 0 && (
                <div>No detailed info available</div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Status</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Online: {networkStatus.isOnline ? 'Yes' : 'No'}</div>
              <div>Monitoring: {isMonitoring ? 'Active' : 'Inactive'}</div>
              {networkStatus.saveData && (
                <div className="text-yellow-600">Data Saver Enabled</div>
              )}
            </div>
          </div>
        </div>

        {/* Network Issues Alert */}
        {!networkStatus.isOnline && (
          <Alert>
            <WifiOff className="h-4 w-4" />
            <AlertDescription>
              Your device is currently offline. Downloads will automatically resume when your connection is restored.
            </AlertDescription>
          </Alert>
        )}

        {networkStatus.connectionQuality === 'poor' && networkStatus.isOnline && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Poor connection quality detected. Downloads may be slower than usual or may fail. 
              Consider switching to a better network if possible.
            </AlertDescription>
          </Alert>
        )}

        {/* Diagnostics Section */}
        {showDiagnostics && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Network Diagnostics</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunDiagnostics}
                disabled={isRunningDiagnostics}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", isRunningDiagnostics && "animate-spin")} />
                {isRunningDiagnostics ? 'Testing...' : 'Run Test'}
              </Button>
            </div>

            {diagnostics.lastUpdated && (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="space-y-1">
                  <div className="text-muted-foreground">DNS Latency</div>
                  <div className="font-medium">
                    {formatLatency(diagnostics.dnsLatency)}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">HTTP Latency</div>
                  <div className="font-medium">
                    {formatLatency(diagnostics.httpLatency)}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">Download Speed</div>
                  <div className="font-medium">
                    {formatSpeed(diagnostics.downloadSpeed)}
                  </div>
                </div>
              </div>
            )}

            {diagnostics.errors.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-red-600">Diagnostic Issues:</div>
                <div className="space-y-1">
                  {diagnostics.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diagnostics.lastUpdated && (
              <div className="text-xs text-muted-foreground">
                Last updated: {diagnostics.lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          {!isMonitoring && (
            <Button onClick={startMonitoring} variant="outline" size="sm">
              <Activity className="h-4 w-4 mr-2" />
              Start Monitoring
            </Button>
          )}
          
          {isMonitoring && (
            <Button onClick={stopMonitoring} variant="outline" size="sm">
              <Activity className="h-4 w-4 mr-2" />
              Stop Monitoring
            </Button>
          )}

          {networkStatus.isOnline && (
            <Button
              onClick={() => window.open('https://www.google.com', '_blank')}
              variant="outline"
              size="sm"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Test Connection
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NetworkStatusMonitor; 